import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Check } from 'lucide-react';
import { FlowRoleSchema, type FlowRole } from '@shared/flow/role';
import { TEAM_DISPLAY_ORDER, teamLabel } from '@shared/flow/role-view';

type LensVariant = 'submenu' | 'inline';

export interface TeamLensItemsProps {
  team: FlowRole | null;
  onPick: (team: FlowRole) => void;
  variant: LensVariant;
  itemClassName?: string;
}

/**
 * Role view switcher. These are the team radios shared by the desktop avatar menu's
 * "User Role: <Team>" submenu and the mobile hamburger's inline "User Role"
 * group (plan U5, R1). One component feeds both surfaces so copy and
 * order cannot drift between them.
 *
 * Lists every team in `TEAM_DISPLAY_ORDER` (ClayDR, GTME, GS). Picking a
 * team always calls `onPick`, even when it is the rep's real team — the
 * server normalizes "picked my own team" to NULL (Decision 3); this
 * component never special-cases it.
 */
export function TeamLensItems({ team, onPick, variant, itemClassName }: TeamLensItemsProps) {
  const testidPrefix = variant === 'inline' ? 'nav-rail-mobile-team-lens-item' : 'nav-rail-team-lens-item';

  return (
    <DropdownMenu.RadioGroup value={team ?? ''} onValueChange={(next) => {
        const parsed = FlowRoleSchema.safeParse(next);
        if (parsed.success) onPick(parsed.data);
      }}>
      {TEAM_DISPLAY_ORDER.map((option) => (
        <DropdownMenu.RadioItem
          key={option}
          value={option}
          data-testid={`${testidPrefix}-${option}`}
          className={itemClassName}
          onSelect={(e) => e.preventDefault()}
        >
          {teamLabel(option)}
          <DropdownMenu.ItemIndicator className="ml-auto flex items-center justify-center">
            <Check className="w-4 h-4" />
          </DropdownMenu.ItemIndicator>
        </DropdownMenu.RadioItem>
      ))}
    </DropdownMenu.RadioGroup>
  );
}
